<template lang="pug">
.course-metadata-editor.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/home"
        )
          | Back to all courses
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="`#/editor/${baseYamlFilename.slice(0,-4)}`"
        )
          | Back to editor
    .ms-auto
    ToggleDark

  .flex-1.p-12.overflow-y-auto
    .max-w-screen-xl.mx-auto
      .prose.prose.dark_prose-invert.max-w-none
        h1.font-normal
          | Manage Portal Library Metadata for #[b {{ baseYamlFilename }}]
        
        p.opacity-80.italic.text-xl(v-if="!loading")
          span(v-if="publishedData && publishedData.releasedOn")
            | 🟢 Released on {{ formatReleaseDate(publishedData.releasedOn) }}<br>
            | Please note: When updating metadata, the changes will appear in the portal after the nightly sync.
          span(v-else-if="publishedData && Object.keys(publishedData).length > 0 && !publishedData.releasedOn")
            | 🟡 This course is published to the Portal Library but does not have a Released On date.<br>
            | Please note: When updating metadata, the changes will appear in the portal after the nightly sync.
          span(v-else)
            | 🔴 This course is not yet published to the Portal Library.<br>
            | Please note: When updating metadata, the changes will appear in the portal after the nightly sync.

        //- Loading and Error States
        .alert.alert-error.bg-red-100.dark_bg-red-900.border.border-red-400.dark_border-red-700.text-red-700.dark_text-red-400.px-4.py-3.rounded.mb-4(v-if="error")
          | {{ error }}
        
        .alert.alert-info.bg-blue-100.dark_bg-blue-900.border.border-blue-400.dark_border-blue-700.text-blue-700.dark_text-blue-400.px-4.py-3.rounded.mb-4(v-if="loading")
          | Loading course data...

      //- Status Panel
      .panel.prose.dark_prose-invert.max-w-screen-md.mx-auto.bg-gray-100.dark_bg-gray-800.p-6.rounded-lg.my-4.sticky.top-0.z-20.shadow-lg.border.border-gray-200.dark_border-gray-600.screen-xl(v-if="!loading && !isCompact")
        .flex.justify-between.items-start
          div.flex-1.pl-8
            h2.text-xl.font-bold.mb-4 Status
            ul.status-list.list-none.p-0.m-0
              li.flex.justify-between.items-center.mb-2.gap-4.p-2.rounded(v-for="(section, sectionName) in orderedSections" :key="sectionName" :class="{ 'bg-gray-50 dark_bg-gray-700': section.order % 2 === 1 }")
                span.text-gray-900.dark_text-gray-100.font-bold.text-lg {{ section.title }}
                span.font-bold.text-lg(:class="getStatusDisplay(sectionStatus[sectionName]).color") {{ getStatusDisplay(sectionStatus[sectionName]).text }}
            
            .text-right.mt-4
              button.btn.bg-gray-500.text-white.py-2.px-4.rounded.mr-2(
                v-if="showGlobalResetButton"
                @click="openResetModal('all')"
              ) Reset Form
              button.save-btn.bg-primary-600.text-white.py-2.px-4.rounded(
                @click="handlePublishUpdate"
                :disabled="!canPublish"
                :class="canPublish ? 'bg-primary-600' : 'bg-gray-400 opacity-50 cursor-not-allowed'"
              ) {{ publishedData && Object.keys(publishedData).length > 0 ? 'Update' : 'Publish' }}
          button.btn.btn-icon.ripple.ripple--centered.text-gray-800.dark_text-gray-200.p-2.text-xl.h-10.items-center.ml-4(
            @click="toggleCompact"
            aria-label="Toggle compact view"
            type="button"
          )
            Icon(icon="teenyicons:down-small-outline")

      //- Compact Status Panel
      .panel.prose.dark_prose-invert.max-w-screen-xl.mx-auto.bg-gray-100.dark_bg-gray-800.p-4.rounded-lg.my-4.sticky.top-0.z-20.shadow-lg.border.border-gray-200.dark_border-gray-600(v-if="!loading && isCompact")
        .flex.justify-between.items-center.h-12
          .flex.space-x-6.items-center
            .flex.items-center.space-x-2(v-for="(section, sectionName) in orderedSections" :key="sectionName")
              span.text-base.font-bold.text-gray-900.dark_text-gray-100 {{ section.order }}
              span.text-sm.font-bold.px-3.py-2.rounded(:class="getStatusBadgeClass(sectionStatus[sectionName])") {{ getStatusDisplay(sectionStatus[sectionName]).text }}
          .flex.items-center.space-x-4
            button.btn.bg-gray-500.text-white.py-2.px-4.rounded.mr-2(
              v-if="showGlobalResetButton"
              @click="openResetModal('all')"
            ) Reset
            button.save-btn.bg-primary-600.text-white.py-2.px-4.rounded(
              @click="handlePublishUpdate"
              :disabled="!canPublish"
              :class="canPublish ? 'bg-primary-600' : 'bg-gray-400 opacity-50 cursor-not-allowed'"
            ) {{ publishedData && Object.keys(publishedData).length > 0 ? 'Update' : 'Publish' }}
            button.btn.btn-icon.ripple.ripple--centered.text-gray-800.dark_text-gray-200.p-2.text-xl.h-10.items-center(
              @click="toggleCompact"
              aria-label="Toggle compact view"
              type="button"
            )
              Icon(icon="teenyicons:up-small-outline")

      //- Reset Confirmation Modal
      .modal-overlay.fixed.inset-0.bg-black.bg-opacity-50.z-50.flex.items-center.justify-center(v-if="showResetModal" @click="closeResetModal")
        .modal-content.prose.dark_prose-invert.max-w-md.mx-auto.bg-gray-100.dark_bg-gray-800.p-6.rounded-lg.shadow-lg.border.border-gray-200.dark_border-gray-600(@click.stop)
          h3.text-xl.font-bold.mb-4 {{ resetModalTitle }}
          p.text-gray-700.dark_text-gray-300.mb-6
            | Resetting will restore the data to the last published version and cannot be undone. 
            | Any changes will be lost.
          .flex.justify-end.space-x-3
            button.btn.bg-gray-500.text-white.py-2.px-4.rounded(
              @click="closeResetModal"
            ) Cancel
            button.reset-confirm-btn.bg-red-600.text-white.py-2.px-4.rounded(
              @click="confirmReset"
            ) Reset

      //- Publish Confirmation Modal
      .modal-overlay.fixed.inset-0.bg-black.bg-opacity-50.z-50.flex.items-center.justify-center(v-if="showPublishModal" @click="closePublishModal")
        .modal-content.prose.dark_prose-invert.max-w-md.mx-auto.bg-gray-100.dark_bg-gray-800.p-6.rounded-lg.shadow-lg.border.border-gray-200.dark_border-gray-600(@click.stop)
          h3.text-xl.font-bold.mb-4(v-if="isUpdateMode") Update {{ metadata.title || 'Course' }} on the Portal Library?
          h3.text-xl.font-bold.mb-4(v-else) Publish {{ metadata.title || 'Course' }} to the Portal Library?
          p.text-gray-700.dark_text-gray-300.mb-6(v-if="isUpdateMode")
            | These updates will take effect IMMEDIATELY in the Vinci Portal Library. Please double check that you have not changed fields with significant meaning like suite, topic, industry, retired, or exclude from demo.
          div.bg-gray-50.dark_bg-gray-700.rounded.p-3.mb-4(v-if="isUpdateMode && Object.keys(publishDiff).length > 0")
            h4.text-base.font-bold.mb-2 Changes to be applied
            div.space-y-2
              div(v-for="(diffs, sectionKey) in publishDiff" :key="sectionKey")
                details
                  summary.cursor-pointer.font-semibold {{ SECTION_CONFIG[sectionKey].title }} ({{ diffs.length }})
                  ul.mt-2.ml-4
                    li(v-for="d in diffs" :key="sectionKey + d.field")
                      span.font-semibold {{ d.field }}:
                      span.ml-1.text-red-600.line-through(v-if="d.oldVal !== undefined") {{ Array.isArray(d.oldVal) ? d.oldVal.join(', ') : stripHtml(d.oldVal) }}
                      span.ml-1.text-gray-500(v-if="d.oldVal !== undefined") →
                      span.ml-1.text-green-700 {{ Array.isArray(d.newVal) ? d.newVal.join(', ') : stripHtml(d.newVal) }}
          p.text-gray-700.dark_text-gray-300.mb-6(v-if="isUpdateMode") If you are unsure, hit cancel and reset form.
          p.text-gray-700.dark_text-gray-300.mb-6(v-if="isUpdateMode") If you are sure, please hit Update.
          div(v-else)
            p.text-gray-700.dark_text-gray-300.mb-6
              | This is a first-time publish which will add this course LIVE to the Vinci Portal Library. This will then be visible to all clients.
            p.text-gray-700.dark_text-gray-300.mb-6 Are you sure you want to continue?
          .flex.justify-end.space-x-3
            button.btn.bg-gray-500.text-white.py-2.px-4.rounded(
              @click="closePublishModal"
              :disabled="isPublishing"
            ) Cancel
            button.reset-confirm-btn.bg-red-600.text-white.py-2.px-4.rounded.flex.items-center(
              @click="confirmPublish"
              :disabled="isPublishing"
            )
              span.mr-2(v-if="isPublishing")
                Icon(icon="mdi:loading" class="animate-spin")
              span {{ isPublishing ? (isUpdateMode ? 'Updating...' : 'Publishing...') : (isUpdateMode ? 'Update' : 'Publish') }}

      //- Section 1: Course Title
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionTitle.order}. ${SECTION_CONFIG.sectionTitle.title}`"
        sectionName="sectionTitle"
        :isSaving="savingSections.sectionTitle"
        :canSave="canSaveSection('sectionTitle')"
        :canReset="canResetSection('sectionTitle')"
        @save="saveSection('sectionTitle')"
        @reset="openResetModal('sectionTitle')"
      )
        .grid.grid-cols-2.gap-6.mb-6
          div
            .flex.items-center.gap-3.mb-2
              label.block(for="title")
                | Course Title 
                span.text-red-500.required-asterisk *
              .flex.items-center.gap-2
                input.w-4.h-4.text-primary-600.bg-gray-100.border-gray-300.rounded.focus_ring-primary-500.dark_focus_ring-primary-500.dark_ring-offset-gray-700.focus_ring-2.dark_bg-gray-600.dark_border-gray-500(
                  type="checkbox"
                  id="titleSyncToggle"
                  v-model="titleSyncEnabled"
                )
                label.text-sm.text-gray-700.dark_text-gray-300(for="titleSyncToggle")
                  | Course Title and Portal Display Title are the same
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(type="text" id="title" v-model="metadata.title" placeholder="Enter course title")
            p.text-sm.text-gray-500.dark_text-gray-400.mt-2.mb-0
              | By default, the Course Title and Portal Display Title are the same. If adding metadata in brackets to the Course Title, please ensure you:<br>1) uncheck the box above; and<br>2) remove the metadata from the Portal Display Title.<br><br>This will keep it clean in the portal, but leave the title with the extra data so third-party LMS platforms and partners can easily distinguish title variants. <br><br>Please note: When saving new topics, translations will be run on all languages available for that topic. 
          div(v-if="!titleSyncEnabled")
            label.block.mb-2(for="portalTitle")
              | Portal Display Title
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(type="text" id="portalTitle" v-model="metadata.portalTitle" placeholder="How should the title appear in Portal?")
        .grid.grid-cols-2.gap-6.mb-6(v-if="false")
          div
            label.block.mb-2(for="vendor")
              | Vendor 
              span.text-gray-500.text-sm.ml-2 (Currently disabled)
            input.w-full.p-2.bg-gray-100.dark_bg-gray-600.border.border-gray-300.dark_border-gray-600.rounded.text-gray-500.dark_text-gray-400(
              type="text" 
              id="vendor" 
              v-model="metadata.vendor"
              disabled
              :class="{ 'opacity-50 cursor-not-allowed' : true }"
            )
        .grid.grid-cols-2.gap-6.mb-6
          div
            label.block.mb-2(for="suiteTitle")
              | Suite 
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="suiteTitle" v-model="metadata.suiteTitle")
              option(value="" disabled) Select suite
              option(v-for="suite in suites" :key="suite.id" :value="suite.title") {{ suite.title }}
          div
            label.block.mb-2(for="topicTitle")
              | Topic 
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="topicTitle" v-model="metadata.topicTitle")
              option(value="" disabled) Select topic
              option(v-for="topic in filteredTopics" :key="topic.id" :value="topic.title") {{ topic.title }}
        .mb-6(v-if="showSubtopicField")
          MultiSelectDropdown(
            v-model="subtopicSelectionModel"
            :options="subtopicOptionLabels"
            :dataType="'array'"
            label="Subtopic"
            :required="true"
            placeholder="Select subtopics"
          )

      //- Section 2: Course Type & Features
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionFeatures.order}. ${SECTION_CONFIG.sectionFeatures.title}`"
        sectionName="sectionFeatures"
        :isSaving="savingSections.sectionFeatures"
        :canSave="canSaveSection('sectionFeatures')"
        :canReset="canResetSection('sectionFeatures')"
        @save="saveSection('sectionFeatures')"
        @reset="openResetModal('sectionFeatures')"
      )
        .grid.grid-cols-3.gap-6.mb-6
          div
            label.block.mb-2(for="activityName")
              | Course Type 
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="activityName" v-model="metadata.activityName")
              option(value="" disabled) Choose...
              option(v-for="activityType in activityTypes" :key="activityType" :value="activityType") {{ activityType }}
          div
            label.block.mb-2(for="completionCriteria")
              | Assessment 
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="completionCriteria" v-model="metadata.resourceMaster.CompletionCriteria")
              option(:value="null" disabled) Select type
              option(:value="1") View All Pages
              option(:value="2") Scored Assessment
          div(v-if="metadata.resourceMaster.CompletionCriteria === 2")
            label.block.mb-2(for="passMark")
              | Passmark 
              span.text-red-500.required-asterisk *
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(type="number" id="passMark" v-model="metadata.passMark" placeholder="e.g. 80" min="0" max="100")
        .grid.grid-cols-2.gap-6.mb-6
          div
            label.block.mb-2(for="level")
              | Level 
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="level" v-model="metadata.level")
              option(value="" disabled) Select level
              option All Levels
              option Intermediate
              option Advanced
          div
            label.block.mb-2(for="duration")
              | Duration (HH:MM) 
              span.text-red-500.required-asterisk *
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              type="text" 
              id="duration" 
              v-model="metadata.duration" 
              placeholder="00:30"
              pattern="[0-9]{2}:[0-9]{2}"
              title="Please enter the duration in HH:MM format."
            )
            p.text-sm.text-gray-500.dark_text-gray-400.mt-2.mb-0 According to our style guide, courses that are an hour or over should be displayed as 01:00.
        .grid.grid-cols-2.gap-6.mb-6
          div
            MultiSelectDropdown(
              v-model="metadata.accredited"
              :options="accreditations"
              :dataType="'comma-separated'"
              label="Accredited"
              placeholder="Select accreditations"
            )
          div
            MultiSelectDropdown(
              v-model="metadata.additionalInfo.CourseFeatures"
              :options="courseFeatures"
              :dataType="'array'"
              label="Course Features"
              placeholder="Select course features"
            )
        .grid.grid-cols-2.gap-6.mb-6(v-if="isHKLawSocietySelected")
          div
            label.block.mb-2(for="hkcpdHours")
              | HK CPD Hours
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="hkcpdHours" v-model="metadata.hkcpdHours")
              option(:value="null" disabled) Select hours
              option(:value="0") 0
              option(:value="0.5") 0.5
              option(:value="1") 1
              option(:value="1.5") 1.5
              option(:value="2") 2
              option(:value="2.5") 2.5
              option(:value="3") 3
          div
            label.block.mb-2(for="hkrmeHours")
              | HK RME Hours
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="hkrmeHours" v-model="metadata.hkrmeHours")
              option(:value="null" disabled) Select hours
              option(:value="0") 0
              option(:value="0.5") 0.5
              option(:value="1") 1
              option(:value="1.5") 1.5
              option(:value="2") 2
              option(:value="2.5") 2.5
              option(:value="3") 3

      //- Section 3: Description & Objectives
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionDescriptions.order}. ${SECTION_CONFIG.sectionDescriptions.title}`"
        sectionName="sectionDescriptions"
        :isSaving="savingSections.sectionDescriptions"
        :canSave="canSaveSection('sectionDescriptions')"
        :canReset="canResetSection('sectionDescriptions')"
        @save="saveSection('sectionDescriptions')"
        @reset="openResetModal('sectionDescriptions')"
      )
        label.block.mb-0(for="tagline")
          | Tagline 
          span.text-red-500.required-asterisk *
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | Please write a tagline for the Portal.
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | This must be one continuous paragraph.
        textarea.w-full.p-2.mb-4.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="tagline" v-model="taglineText" rows="2" placeholder="Write a short summary for marketing.")
        label.block.mb-0(for="description")
          | Description 
          span.text-red-500.required-asterisk *
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | Please write 60-100 words about the course. 
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | You can write multiple paragraphs by leaving a blank line between them.
        textarea.w-full.p-2.mb-4.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="description" v-model="descriptionText" rows="4" placeholder="Write a course summary.")
        label.block.mb-0(for="shortDescription")
          | Short Description 
          span.text-red-500.required-asterisk *
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | Please write a short description to be used for marketing metadata, SEO, and the website. (150-160 characters)
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | This must be one continuous paragraph.
        textarea.w-full.p-2.mb-4.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="shortDescription" v-model="shortDescriptionText" rows="2" placeholder="Write a short description for marketing and the website.")
        label.block.mb-0(for="objectives")
          | Objectives 
          span.text-red-500.required-asterisk *
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | What are the learning objectives of the course?
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | Each objective should be on its own line preceded by a dash and with no full stop.
        textarea.w-full.p-2.mb-4.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="objectives" v-model="objectivesText" rows="4" placeholder="- Learning Objective 1")
        label.block.mb-0(for="howTaken")
          | How taken 
          span.text-red-500.required-asterisk *
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | Is this course an overview, a refresher, a deeper dive, part of an induction, etc.?
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | This must be one continuous paragraph.
        textarea.w-full.p-2.mb-4.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="howTaken" v-model="howTakenText" rows="3" placeholder="How does this fit in a learning plan?")

      //- Section 4: Who Is It For?
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionAudience.order}. ${SECTION_CONFIG.sectionAudience.title}`"
        sectionName="sectionAudience"
        :isSaving="savingSections.sectionAudience"
        :canSave="canSaveSection('sectionAudience')"
        :canReset="canResetSection('sectionAudience')"
        @save="saveSection('sectionAudience')"
        @reset="openResetModal('sectionAudience')"
      )
        .grid.grid-cols-2.gap-6.mb-6
          div
            label.block.mb-0(for="industry")
              | Industry 
              span.text-red-500.required-asterisk *
            HierarchicalIndustrySelect(
              v-model="metadata.additionalInfo.industryId"
              :industries="industries"
              placeholder="Select industry"
              instructionalText="Please select the lowest level industry that applies to this course."
              @change="handleIndustryChange"
            )
        div.mb-6
          HierarchicalMultiSelectDropdown(
            v-model="metadata.additionalInfo.Jurisdiction"
            @update:jurisdictionIds="metadata.additionalInfo.jurisdictionId = $event"
            :jurisdictions="jurisdictions"
            :dataType="'array'"
            label="Jurisdiction"
            :required="true"
            placeholder="Select jurisdictions"
            instructionalText="Please select all of the applicable jurisdictions."
          )
          div.mb-6
            MultiSelectDropdown(
              v-model="metadata.resourceMaster.TargetLearners"
              :options="targetLearners"
              :dataType="'array'"
              label="Target Learners"
              :required="true"
              placeholder="Select target learners"
            )

      //- Section 5: Additional Info
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionInfo.order}. ${SECTION_CONFIG.sectionInfo.title}`"
        sectionName="sectionInfo"
        :isSaving="savingSections.sectionInfo"
        :canSave="canSaveSection('sectionInfo')"
        :canReset="canResetSection('sectionInfo')"
        @save="saveSection('sectionInfo')"
        @reset="openResetModal('sectionInfo')"
      )
        .grid.grid-cols-2.gap-6.mb-6(v-if="false")
          div
            label.block.mb-2(for="presentersBio")
              | Presenter's Bio 
              span.text-gray-500.text-sm.ml-2 (Currently disabled)
            textarea.w-full.p-2.bg-gray-100.dark_bg-gray-600.border.border-gray-300.dark_border-gray-600.rounded.text-gray-500.dark_text-gray-400(
              id="presentersBio" 
              v-model="metadata.resourceMaster.PresentersBio" 
              rows="3"
              disabled
              :class="{ 'opacity-50 cursor-not-allowed' : true }"
            )
          div
            label.block.mb-2(for="version")
              | Version 
              span.text-gray-500.text-sm.ml-2 (Currently disabled)
              span.text-sm.text-gray-500.dark_text-gray-400.ml-2(v-if="metadata.retired") (Auto-managed)
            input.w-full.p-2.bg-gray-100.dark_bg-gray-600.border.border-gray-300.dark_border-gray-600.rounded.text-gray-500.dark_text-gray-400(
              type="text" 
              id="version" 
              v-model="metadata.resourceMaster.Version" 
              placeholder="e.g. 1.0"
              disabled
              :class="{ 'opacity-50 cursor-not-allowed' : true }"
            )
            p.text-sm.text-gray-500.dark_text-gray-400.mt-1(v-if="metadata.retired") Version is auto-managed when course is retired
        .grid.grid-cols-2.gap-6.mt-6
          div
            label.block.mb-2(for="retired")
              | Retired
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="retired" v-model="metadata.retired")
              option(:value="false") No
              option(:value="true") Yes
          div(v-if="metadata.retired")
            label.block.mb-2(for="replacedBy")
              | Replaced by
              span.text-sm.text-gray-500.dark_text-gray-400.ml-2 (Only when retired)
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(type="number" id="replacedBy" v-model="metadata.replacedBy" placeholder="Enter course ID number")
        .grid.grid-cols-2.gap-6.mt-6
          div
            label.block.mb-2(for="flagship")
              | Flagship
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="flagship" v-model="metadata.flagship")
              option(value="false") No
              option(value="true") Yes
          div
            label.block.mb-2(for="excludeFromDemo")
              | Hide From Website
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="excludeFromDemo" v-model="metadata.additionalInfo.ExcludeFromDemo")
              option(:value="false") No
              option(:value="true") Yes
        .grid.grid-cols-2.gap-6.mt-6
          div
            label.block.mb-2(for="triggerWarning")
              | Trigger Warning
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="triggerWarning" v-model="metadata.additionalInfo.triggerWarning")
              option(:value="false") No
              option(:value="true") Yes
          div
            label.block.mb-2(for="qualityIndex")
              | Quality Index
              span.text-sm.text-gray-500.dark_text-gray-400.ml-2 (1 - Worst / 5 - Best)
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(id="qualityIndex" v-model="metadata.additionalInfo.qualityIndex")
              option(value="" disabled) Select Quality Index
              option(value="1") 1 - Worst
              option(value="2") 2
              option(value="3") 3
              option(value="4") 4
              option(value="5") 5 - Best
        
        //- Course Group Selection
        .mt-6
          CourseIdSelect(
            v-model="metadata.additionalInfo.groupCourseId"
            :courses="transformedLibraryCourses"
            label="Course Group"
            instructionalText="Is this course a variation of another course? Select the original course ID here. If this course stands alone, leave this field empty - it will use its own ID automatically."
            placeholder="Search for a course..."
            :disabled="loadingLibraryCourses"
            :multiple="false"
            valueType="single"
          )

        //- Related To Selection
        .mt-6
          CourseIdSelect(
            v-model="metadata.additionalInfo.relatedTo"
            :courses="transformedLibraryCourses"
            label="Related To"
            instructionalText="Select courses with similar objectives/duration/audience etc."
            placeholder="Search for courses..."
            :disabled="loadingLibraryCourses"
            :multiple="true"
            valueType="array"
          )

        //- Redundancy Selection
        .mt-6
          CourseIdSelect(
            v-model="metadata.additionalInfo.redundancy"
            :courses="transformedLibraryCourses"
            label="Redundancy"
            instructionalText="Is this course part of another course? Select their IDs."
            placeholder="Search for courses..."
            :disabled="loadingLibraryCourses"
            :multiple="true"
            valueType="array"
          )

        //- Legislation Field
        .grid.grid-cols-2.gap-6.mt-6
          div
            label.block.mb-0(for="legislation")
              | Legislation
            p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
              | Each piece of legislation should be on its own line preceded by a dash.
            textarea.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              id="legislation" 
              v-model="legislationText" 
              rows="3" 
              placeholder="- Sample Legislation 1"
            )

      //- Section 6: Generate Thumbnails (Custom Section)
      .panel.prose.dark_prose-invert.max-w-screen-xl.mx-auto.bg-gray-100.dark_bg-gray-800.p-6.py-0.rounded-lg.my-4.shadow-lg.border.border-gray-200.dark_border-gray-600(v-if="!loading")
        .flex.justify-between.items-start.mb-1
          div.flex-1
            h2.text-xl.font-bold.my-6 {{ SECTION_CONFIG.sectionThumbnails.order }}. {{ SECTION_CONFIG.sectionThumbnails.title }}
        
        .text-center
          .mb-4
            h3.text-lg.font-semibold.my-1.text-gray-800.dark_text-gray-200
              | Thumbnail Previews
            p.italic.text-gray-500.dark_text-gray-400.mb-4.mt-4 Click on a thumbnail to preview the whole image.
            .grid.grid-cols-2.gap-4.md_grid-cols-4
              //- Standard Thumbnail
              .thumbnail-preview.border.border-gray-300.dark_border-gray-600.rounded-lg.p-3.bg-white.dark_bg-gray-700
                .text-sm.font-medium.mb-2.text-gray-700.dark_text-gray-300 Standard
                .thumbnail-container.h-24.bg-gray-100.dark_bg-gray-600.rounded.flex.items-center.justify-center.cursor-pointer(
                  @click="openThumbnailModal('standard')"
                  :class="{ 'opacity-50': !thumbnailStatuses.standard && !getGeneratedThumbnail('standard') }"
                )
                  template(v-if="getGeneratedThumbnail('standard')")
                    img.h-full.w-full.object-cover.rounded(
                      :src="getGeneratedThumbnail('standard').dataUrl"
                      :alt="`Generated standard thumbnail for course ${props.courseId}`"
                    )
                  template(v-else-if="thumbnailStatuses.standard")
                    img.h-full.w-full.object-cover.rounded(
                      :src="`${thumbnailBaseUrl}/${props.courseId}.jpg?t=${thumbnailCacheBuster}`"
                      :alt="`Standard thumbnail for course ${props.courseId}`"
                      @error="thumbnailStatuses.standard = false"
                    )
                  template(v-else)
                    .text-gray-500.dark_text-gray-400.text-xs.text-center
                      | No thumbnail
                      br
                      | available
              
              //- Hero Thumbnail
              .thumbnail-preview.border.border-gray-300.dark_border-gray-600.rounded-lg.p-3.bg-white.dark_bg-gray-700
                .text-sm.font-medium.mb-2.text-gray-700.dark_text-gray-300 Hero
                .thumbnail-container.h-24.bg-gray-100.dark_bg-gray-600.rounded.flex.items-center.justify-center.cursor-pointer(
                  @click="openThumbnailModal('hero')"
                  :class="{ 'opacity-50': !thumbnailStatuses.hero && !getGeneratedThumbnail('hero') }"
                )
                  template(v-if="getGeneratedThumbnail('hero')")
                    img.h-full.w-full.object-cover.rounded(
                      :src="getGeneratedThumbnail('hero').dataUrl"
                      :alt="`Generated hero thumbnail for course ${props.courseId}`"
                    )
                  template(v-else-if="thumbnailStatuses.hero")
                    img.h-full.w-full.object-cover.rounded(
                      :src="`${thumbnailBaseUrl}/${props.courseId}-hero.jpg?t=${thumbnailCacheBuster}`"
                      :alt="`Hero thumbnail for course ${props.courseId}`"
                      @error="thumbnailStatuses.hero = false"
                    )
                  template(v-else)
                    .text-gray-500.dark_text-gray-400.text-xs.text-center
                      | No thumbnail
                      br
                      | available
              
              //- Tile Thumbnail
              .thumbnail-preview.border.border-gray-300.dark_border-gray-600.rounded-lg.p-3.bg-white.dark_bg-gray-700
                .text-sm.font-medium.mb-2.text-gray-700.dark_text-gray-300 Tile
                .thumbnail-container.h-24.bg-gray-100.dark_bg-gray-600.rounded.flex.items-center.justify-center.cursor-pointer(
                  @click="openThumbnailModal('tile')"
                  :class="{ 'opacity-50': !thumbnailStatuses.tile && !getGeneratedThumbnail('tile') }"
                )
                  template(v-if="getGeneratedThumbnail('tile')")
                    img.h-full.w-full.object-cover.rounded(
                      :src="getGeneratedThumbnail('tile').dataUrl"
                      :alt="`Generated tile thumbnail for course ${props.courseId}`"
                    )
                  template(v-else-if="thumbnailStatuses.tile")
                    img.h-full.w-full.object-cover.rounded(
                      :src="`${thumbnailBaseUrl}/${props.courseId}-tile.jpg?t=${thumbnailCacheBuster}`"
                      :alt="`Tile thumbnail for course ${props.courseId}`"
                      @error="thumbnailStatuses.tile = false"
                    )
                  template(v-else)
                    .text-gray-500.dark_text-gray-400.text-xs.text-center
                      | No thumbnail
                      br
                      | available
              
              //- No Title Thumbnail
              .thumbnail-preview.border.border-gray-300.dark_border-gray-600.rounded-lg.p-3.bg-white.dark_bg-gray-700
                .text-sm.font-medium.mb-2.text-gray-700.dark_text-gray-300 No Title
                .thumbnail-container.h-24.bg-gray-100.dark_bg-gray-600.rounded.flex.items-center.justify-center.cursor-pointer(
                  @click="openThumbnailModal('noTitle')"
                  :class="{ 'opacity-50': !thumbnailStatuses.noTitle && !getGeneratedThumbnail('noTitle') }"
                )
                  template(v-if="getGeneratedThumbnail('noTitle')")
                    img.h-full.w-full.object-cover.rounded(
                      :src="getGeneratedThumbnail('noTitle').dataUrl"
                      :alt="`Generated no title thumbnail for course ${props.courseId}`"
                    )
                  template(v-else-if="thumbnailStatuses.noTitle")
                    img.h-full.w-full.object-cover.rounded(
                      :src="`${thumbnailBaseUrl}/${props.courseId}-notitle.jpg?t=${thumbnailCacheBuster}`"
                      :alt="`No title thumbnail for course ${props.courseId}`"
                      @error="thumbnailStatuses.noTitle = false"
                    )
                  template(v-else)
                    .text-gray-500.dark_text-gray-400.text-xs.text-center
                      | No thumbnail
                      br
                      | available

          .my-6(v-if="!hasGeneratedThumbnails")
            button.btn.py-2.px-4.bg-primary-600.dark_bg-primary-600.hover_bg-primary-700.text-white.rounded.font-medium(
              @click="generateThumbnails"
              :disabled="isGeneratingThumbnails"
              :class="isGeneratingThumbnails ? 'opacity-50 cursor-not-allowed bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'"
            )
              span(v-if="isGeneratingThumbnails") Generating...
              span(v-else) {{ thumbnailExists ? 'Regenerate Thumbnails' : 'Generate Thumbnails' }}

          //- Cancel/Upload buttons (Phase 2)
          .flex.justify-end.space-x-3.my-6(v-if="hasGeneratedThumbnails")
            button.btn.bg-gray-500.text-white.py-2.px-4.rounded.mr-2(
              @click="cancelGeneratedThumbnails"
              :disabled="isUploadingThumbnails"
            ) Cancel
            button.save-btn.py-2.px-4.bg-primary-600.text-white.rounded(
              @click="uploadGeneratedThumbnails"
              :disabled="isUploadingThumbnails || uploadSuccess"
              :class="isUploadingThumbnails ? 'opacity-50 cursor-not-allowed bg-gray-400' : uploadSuccess ? 'bg-green-600' : 'bg-primary-600'"
            )
              span.flex.items-center.gap-2(v-if="isUploadingThumbnails")
                Icon(icon="mdi:loading" class="animate-spin w-4 h-4")
                span Uploading...
              span.flex.items-center.gap-2(v-else-if="uploadSuccess")
                Icon(icon="mdi:check-circle" class="w-4 h-4")
                span Upload Complete!
              span.flex.items-center.gap-2(v-else)
                Icon(icon="mdi:upload" class="w-4 h-4")
                span Upload

          


      //- Thumbnail Modal
      .fixed.inset-0.bg-black.bg-opacity-50.flex.items-center.justify-center.z-50(v-if="showThumbnailModal")
        .bg-white.dark_bg-gray-800.rounded-lg.p-6.max-w-4xl.max-h-90vh.overflow-auto
          .flex.justify-between.items-center.mb-4
            h3.text-xl.font-bold.text-gray-800.dark_text-gray-200
              | {{ selectedThumbnailType }} Thumbnail Preview
            button.text-gray-500.hover_text-gray-700.dark_text-gray-400.dark_hover_text-gray-200(
              @click="closeThumbnailModal"
            )
              svg.w-6.h-6(fill="none" stroke="currentColor" viewBox="0 0 24 24")
                path(stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12")
          
          .text-center
            img.max-w-full.max-h-96.object-contain.rounded.border.border-gray-300.dark_border-gray-600(
              :src="selectedThumbnailUrl"
              :alt="`${selectedThumbnailType} thumbnail for course ${props.courseId}`"
            )
            .mt-4.text-sm.text-gray-600.dark_text-gray-400
              | Course ID: {{ props.courseId }} | Type: {{ selectedThumbnailType }}

      //- Thumbnail Generation Modal
      ThumbnailGenerationModal(
        v-if="showThumbnailGenerationModal"
        :courseId="props.courseId"
        :firmCode="props.firmCode || undefined"
        :showModal="showThumbnailGenerationModal"
        @close="closeThumbnailGenerationModal"
        @thumbnails-generated="handleThumbnailsGenerated"
      )

      //- Delete button at bottom
      .max-w-screen-xl.mx-auto.mt-8.mb-16(v-if="false")
        .text-center(v-if="publishedData && Object.keys(publishedData).length > 0")
          button.reset-confirm-btn.bg-red-600.text-white.py-2.px-6.rounded(
            @click="openDeleteModal"
          ) Delete

      //- Delete Confirmation Modal
      .modal-overlay.fixed.inset-0.bg-black.bg-opacity-50.z-50.flex.items-center.justify-center(v-if="showDeleteModal" @click="closeDeleteModal")
        .modal-content.prose.dark_prose-invert.max-w-md.mx-auto.bg-gray-100.dark_bg-gray-800.p-6.rounded-lg.shadow-lg.border.border-gray-200.dark_border-gray-600(@click.stop)
          h3.text-xl.font-bold.mb-4 Delete {{ metadata.title || 'Course' }} from Portal Library?
          p.text-gray-700.dark_text-gray-300.mb-6
            | This process will remove this course from the Portal Library. This should only be done during extreme or urgent situations.
          p.text-gray-700.dark_text-gray-300.mb-6 Are you sure you want to continue?
          .flex.flex-wrap.justify-end.gap-3.mt-4.px-2
            button.reset-btn-red.bg-gray-500.text-white.py-2.px-4.rounded(
              @click="closeDeleteModal"
              :disabled="isDeleting"
          ) Cancel
            button.reset-confirm-btn.bg-red-700.text-white.py-2.px-4.rounded.flex.items-center(
              @click="confirmDelete"
              :disabled="isDeleting"
            )
              span.mr-2(v-if="isDeleting")
                Icon(icon="mdi:loading" class="animate-spin")
              span {{ isDeleting ? 'Deleting...' : 'Delete' }}

</template>

<script setup lang="ts">
import { reactive, computed, ref, onMounted, watch, type Ref } from 'vue';
import MainMenu from './MainMenu.vue';
import ToggleDark from './ToggleDark.vue';
import { Icon } from '@iconify/vue';
import FormSection from './FormSection.vue';
import HierarchicalIndustrySelect from './HierarchicalIndustrySelect.vue';
import HierarchicalMultiSelectDropdown from './HierarchicalMultiSelectDropdown.vue';
import MultiSelectDropdown from './MultiSelectDropdown.vue';
import CourseIdSelect from './CourseIdSelect.vue';
import ThumbnailGenerationModal from './ThumbnailGenerationModal.vue';
import {
  getSuitesAndTopics,
  getActivityTypes,
  getAccreditations,
  getIndustries,
  getJurisdictions,
  getTargetLearners,
  getCourseFromLibrary,
  getAllCoursesFromLibrary,
  getDraftMetadata,
  updateDraftMetadataSection,
  publishCourseToLibrary,
  updateCourseInLibrary,
  deleteCourseFromLibrary,
  checkThumbnailExists,
  checkAllThumbnailsExist,
  uploadThumbnail,
  getThumbnailBucketUrl
} from '../lib/callModContentAuthors';
import { loadCourse } from '../store/courses';
import { FIELD_CONFIG, SECTION_CONFIG } from '../lib/courseMetadataConfig';
import courseAccreditationTemplates from '../lib/courseAccreditationTemplates.json';
import {
  getSubtopicsForTopicId,
  normalizeSectionTitleSubtopicFields,
  topicHasSubtopics
} from '../lib/topicSubtopicUtils';
import {
  convertHtmlToText,
  convertTextToHtml,
  compareObjects,
  setNestedValue,
  getNestedValue,
  stripHtml,
  generateUUID
} from '../lib/metadataUtils';
const props = defineProps<{
  courseId: number;
  firmCode?: string;
}>();

const lastUpdated = ref('a month ago'); // Placeholder
const loading = ref(true);
const error = ref('');

// Published data from mod-library-v2
const publishedData = ref<any>(null);

// Draft data from mod-content
const draftData = ref<any>(null);

// YAML title fallback when no metadata exists
const yamlTitle = ref<string>('');

// Dropdown data
const suites = ref<any[]>([]);
const topics = ref<any[]>([]);
const activityTypes = ref<string[]>([]);
const industries = ref<any>({});
const jurisdictions = ref<any>({});
const targetLearners = ref<string[]>([]);
const accreditations = ref<string[]>([]);
const courseFeatures = ref<string[]>([
  'course builder',
  'video',
  'case studies',
  'gamification'
]);

// All library courses for relationship selection
const allLibraryCourses = ref<any[]>([]);
const loadingLibraryCourses = ref(false);

// Transform library courses for component use
const transformedLibraryCourses = computed(() => {
  console.log(
    'Transforming courses:',
    allLibraryCourses.value.length,
    'courses'
  );
  const transformed = allLibraryCourses.value
    .map(course => ({
      id: course.id,
      courseId: course.vinciResourceId,
      title: course.title
    }))
    .sort((a, b) => a.courseId - b.courseId); // Sort by courseId (vinciResourceId)
  console.log('Transformed courses:', transformed.length, 'courses');
  console.log('First transformed course:', transformed[0]);
  return transformed;
});

// Computed properties for HTML-to-text conversion
const taglineText = computed({
  get: () => convertHtmlToText(metadata.tagline, 'single-paragraph'),
  set: value => {
    metadata.tagline = convertTextToHtml(value, 'single-paragraph');
  }
});

const descriptionText = computed({
  get: () => convertHtmlToText(metadata.description, 'paragraph'),
  set: value => {
    metadata.description = convertTextToHtml(value, 'paragraph');
  }
});

const shortDescriptionText = computed({
  get: () => convertHtmlToText(metadata.shortDescription, 'single-paragraph'),
  set: value => {
    metadata.shortDescription = convertTextToHtml(value, 'single-paragraph');
  }
});

const objectivesText = computed({
  get: () => convertHtmlToText(metadata.objectives, 'list'),
  set: value => {
    metadata.objectives = convertTextToHtml(value, 'list');
  }
});

const howTakenText = computed({
  get: () => convertHtmlToText(metadata.howTaken, 'single-paragraph'),
  set: value => {
    metadata.howTaken = convertTextToHtml(value, 'single-paragraph');
  }
});

const legislationText = computed({
  get: () => convertHtmlToText(metadata.additionalInfo.legislation, 'list'),
  set: value => {
    metadata.additionalInfo.legislation = convertTextToHtml(value, 'list');
  }
});

// Helper to set a value on a nested object path (now imported from metadataUtils)

// Generate the initial form structure and default values from the config
const generateDefaults = () => {
  const defaults = {};
  for (const path in FIELD_CONFIG) {
    setNestedValue(defaults, path, FIELD_CONFIG[path].defaultValue);
  }
  return defaults;
};

// Define the complete default structure from config
const formDefaults = generateDefaults();

// Combined form data (draft overrides published)
const metadata = reactive(JSON.parse(JSON.stringify(formDefaults))); // Deep copy

// Get sections in order for dynamic rendering
const orderedSections = computed(() => {
  return Object.fromEntries(
    Object.entries(SECTION_CONFIG).sort(([, a], [, b]) => a.order - b.order)
  );
});

// Helper to get a value from a nested object path (now imported from metadataUtils)
// Note: getNestedValue is imported, but we'll wrap it to pass FIELD_CONFIG
const getNestedValueWithDefaults = (obj: any, path: string) => {
  return getNestedValue(obj, path, FIELD_CONFIG);
};

// Get all field paths for a specific section from the config
const getFieldsForSection = (sectionName: string) => {
  return Object.keys(FIELD_CONFIG).filter(
    path => FIELD_CONFIG[path].section === sectionName
  );
};

const baseYamlFilename = computed(() =>
  props.firmCode
    ? `${props.firmCode}_${props.courseId}.yml`
    : `${props.courseId}.yml`
);

// Format release date for display
const formatReleaseDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
  } catch (error) {
    console.error('Error formatting release date:', error);
    return dateString; // Fallback to original string if parsing fails
  }
};

// Filter topics based on selected suite
const filteredTopics = computed(() => {
  if (!metadata.suiteTitle) return [];

  const selectedSuite = suites.value.find(
    suite => suite.title === metadata.suiteTitle
  );
  if (!selectedSuite) return [];

  return topics.value.filter(
    topic => topic.internalParentId === selectedSuite.internalId
  );
});

const selectedTopicInternalId = computed(() => {
  const t = topics.value.find(topic => topic.title === metadata.topicTitle);
  return t?.internalId ?? null;
});

const subtopicOptions = computed(() =>
  getSubtopicsForTopicId(selectedTopicInternalId.value)
);

const showSubtopicField = computed(() =>
  topicHasSubtopics(selectedTopicInternalId.value)
);

const subtopicOptionLabels = computed(() =>
  subtopicOptions.value.map(o => o.label)
);

/** MultiSelectDropdown uses option labels; metadata stores parallel subtopicId + subtopicTitle arrays. */
const subtopicSelectionModel = computed({
  get(): string[] {
    if (!Array.isArray(metadata.subtopicId)) {
      return [];
    }
    const opts = subtopicOptions.value;
    return metadata.subtopicId.map((id: string) => {
      const opt = opts.find(o => o.value === id);
      return opt ? opt.label : id;
    });
  },
  set(labels: string[] | string) {
    const list = Array.isArray(labels)
      ? labels
      : typeof labels === 'string' && labels
        ? [labels]
        : [];
    if (!topicHasSubtopics(selectedTopicInternalId.value)) {
      metadata.subtopicId = [];
      metadata.subtopicTitle = [];
      return;
    }
    const opts = subtopicOptions.value;
    const ids: string[] = [];
    const names: string[] = [];
    list.forEach(label => {
      const opt = opts.find(o => o.label === label);
      if (opt) {
        ids.push(opt.value);
        names.push(opt.label);
      }
    });
    metadata.subtopicId = ids;
    metadata.subtopicTitle = names;
  }
});

// Check if HK Law Society is selected in Accredited
const isHKLawSocietySelected = computed(() => {
  if (!metadata.accredited) return false;

  // Handle both string and array formats - both use includes()
  return metadata.accredited.includes('HK Law Society');
});

const SECTION_STATUSES = {
  NOT_STARTED: 'not-started',
  READY_TO_PUBLISH: 'ready-to-publish',
  READY_TO_UPDATE: 'ready-to-update',
  PUBLISHED: 'published',
  UNSAVED_CHANGES: 'unsaved-changes'
};

const sectionStatus = reactive({
  sectionTitle: SECTION_STATUSES.NOT_STARTED,
  sectionFeatures: SECTION_STATUSES.NOT_STARTED,
  sectionDescriptions: SECTION_STATUSES.NOT_STARTED,
  sectionAudience: SECTION_STATUSES.NOT_STARTED,
  sectionInfo: SECTION_STATUSES.NOT_STARTED,
  sectionThumbnails: SECTION_STATUSES.NOT_STARTED
});

// Loading states for each section
const savingSections = reactive({
  sectionTitle: false,
  sectionFeatures: false,
  sectionDescriptions: false,
  sectionAudience: false,
  sectionInfo: false,
  sectionThumbnails: false
});

// Store original passmark value to restore when switching back to Scored Assessment
const originalPassmark = ref(0);

// Title sync toggle state
const titleSyncEnabled = ref(true);

// Watch for changes in CompletionCriteria and set passMark to 0 if it's 1
watch(
  () => metadata.resourceMaster.CompletionCriteria,
  (newVal, oldVal) => {
    // Only run if we have a valid value (not null/undefined)
    if (newVal === 1) {
      // Store current value before setting to 0
      if (metadata.passMark > 0) {
        originalPassmark.value = metadata.passMark;
      }
      metadata.passMark = 0;
    } else if (newVal === 2 && oldVal === 1) {
      // Switching back to Scored Assessment from View All Pages
      metadata.passMark = originalPassmark.value;
    }
  }
);

// Watch for changes in suite and clear topic if suite changes
watch(
  () => metadata.suiteTitle,
  (newVal, oldVal) => {
    if (newVal !== oldVal && oldVal) {
      metadata.topicTitle = '';
      metadata.subtopicId = [];
      metadata.subtopicTitle = [];
    }
  }
);

watch(
  () => metadata.topicTitle,
  (newVal, oldVal) => {
    if (oldVal !== undefined && newVal !== oldVal) {
      metadata.subtopicId = [];
      metadata.subtopicTitle = [];
    }
  }
);

// Watch for changes in retired and reset replacedBy when retired is false
watch(
  () => metadata.retired,
  newVal => {
    if (!newVal) {
      metadata.replacedBy = null;
      metadata.resourceMaster.Version = 'Current';
    } else {
      metadata.resourceMaster.Version = 'Retired';
    }
  },
  { immediate: true }
);

// Watch for title sync toggle changes
watch(
  () => titleSyncEnabled.value,
  newValue => {
    if (newValue) {
      // When sync is enabled, copy course title to portal title
      metadata.portalTitle = metadata.title;
    }
  }
);

// Watch for course title changes when sync is enabled
watch(
  () => metadata.title,
  newTitle => {
    if (titleSyncEnabled.value) {
      metadata.portalTitle = newTitle;
    }
  }
);

// Flag to track if initial data loading is complete
const isDataLoaded = ref(false);

// Track thumbnail existence for status calculation
const thumbnailExists = ref(false);
const thumbnailStatuses = reactive({
  standard: false,
  hero: false,
  tile: false,
  noTitle: false
});

// Environment-aware thumbnail base URL
const thumbnailBaseUrl = computed(() => getThumbnailBucketUrl());

// Cache-busting timestamp for thumbnail refresh
const thumbnailCacheBuster = ref(Date.now());

// Thumbnail modal state
const showThumbnailModal = ref(false);
const selectedThumbnailType = ref('');
const selectedThumbnailUrl = ref('');

// Thumbnail generation state
const isGeneratingThumbnails = ref(false);

// Generated thumbnails state (Phase 2)
const generatedThumbnails = ref<any[]>([]);
const hasGeneratedThumbnails = computed(
  () => generatedThumbnails.value.length > 0
);

// Upload state
const isUploadingThumbnails = ref(false);
const uploadSuccess = ref(false);

// Helper function to get generated thumbnail by type
const getGeneratedThumbnail = (type: string) => {
  return generatedThumbnails.value.find(thumb => thumb.type === type);
};

// Thumbnail modal functions
function openThumbnailModal(thumbnailType: string) {
  console.log('openThumbnailModal called with:', thumbnailType);

  // Check if we have a generated thumbnail first, then S3 thumbnail
  const generatedThumb = getGeneratedThumbnail(thumbnailType);
  const hasS3Thumbnail = thumbnailStatuses[thumbnailType];

  if (!generatedThumb && !hasS3Thumbnail) {
    console.log('No thumbnail available:', thumbnailType);
    return; // Don't open modal if no thumbnail exists
  }

  selectedThumbnailType.value =
    thumbnailType.charAt(0).toUpperCase() + thumbnailType.slice(1);

  // Use generated thumbnail if available, otherwise use S3 URL
  if (generatedThumb) {
    selectedThumbnailUrl.value = generatedThumb.dataUrl;
    console.log('Using generated thumbnail for modal');
  } else {
    // Build the S3 URL based on thumbnail type
    const filename =
      thumbnailType === 'standard'
        ? `${props.courseId}.jpg`
        : `${props.courseId}-${
            thumbnailType === 'noTitle' ? 'notitle' : thumbnailType
          }.jpg`;

    selectedThumbnailUrl.value = `${thumbnailBaseUrl.value}/${filename}`;
    console.log('Using S3 thumbnail for modal');
  }

  showThumbnailModal.value = true;
  console.log('Modal should be opening now');
}

// Debug: Add function to window for testing
// @ts-ignore
window.openThumbnailModal = openThumbnailModal;

function closeThumbnailModal() {
  showThumbnailModal.value = false;
  selectedThumbnailType.value = '';
  selectedThumbnailUrl.value = '';
}

// Thumbnail generation function (Phase 3 implementation)
function generateThumbnails() {
  console.log('Generate Thumbnails clicked - Opening generation modal');
  openThumbnailGenerationModal();
}

// Simple watcher for HK Law Society dependency - only runs after data is loaded
watch(
  () => metadata.accredited,
  newVal => {
    // Only run after initial data loading is complete
    if (!isDataLoaded.value) return;

    const isHKSelected = newVal && newVal.includes('HK Law Society');

    if (isHKSelected) {
      // HK Law Society is selected - ensure hours are set to 0 if they're null
      if (metadata.hkcpdHours === null) metadata.hkcpdHours = 0;
      if (metadata.hkrmeHours === null) metadata.hkrmeHours = 0;
    } else {
      // HK Law Society is not selected - set hours to null
      metadata.hkcpdHours = null;
      metadata.hkrmeHours = null;
    }
  }
);

const handleIndustryChange = (selection: { id: string; name: string }) => {
  // Update both the ID and name in metadata
  metadata.additionalInfo.industryId = selection.id;
  metadata.additionalInfo.Industry = selection.name;
};

// UUID generation (now imported from metadataUtils)

// Helper function to ensure CourseversionID exists
const ensureCourseversionID = () => {
  // If CourseversionID doesn't exist in metadata, generate one
  if (!metadata.resourceMaster.CourseversionID) {
    metadata.resourceMaster.CourseversionID = generateUUID();
    console.log(
      'Generated new CourseversionID:',
      metadata.resourceMaster.CourseversionID
    );
  } else {
    console.log(
      'Using existing CourseversionID:',
      metadata.resourceMaster.CourseversionID
    );
  }
  return metadata.resourceMaster.CourseversionID;
};

// HTML to text conversion utilities (now imported from metadataUtils)

// Watch for form changes to update save button states and section statuses
watch(
  metadata,
  async () => {
    // Trigger reactivity update for save button states
    // The hasSectionChanges function will automatically recalculate
    // Also update section statuses to reflect current state
    await updateSectionStatus();
  },
  { deep: true }
);

// Fetch published data from mod-library-v2
const fetchPublishedData = async () => {
  try {
    const response = await getCourseFromLibrary(props.courseId);
    publishedData.value = response.data;
    return publishedData.value;
  } catch (err) {
    console.error('Error fetching published data:', err);
    error.value = `Failed to load published data: ${err.message}`;
    return null;
  }
};

// Fetch draft data from mod-content
const fetchDraftData = async () => {
  try {
    const result = await getDraftMetadata(props.courseId);
    draftData.value = result;
    return draftData.value;
  } catch (err) {
    console.error('Error fetching draft data:', err);
    error.value = `Failed to load draft data: ${err.message}`;
    return null;
  }
};

// Fetch YAML title as fallback when no metadata exists
const fetchYamlTitle = async () => {
  try {
    const course = await loadCourse(props);
    if (course?.latest?.title) {
      yamlTitle.value = course.latest.title;
      // YAML title fetched successfully
    } else {
      // No YAML title found in course data
    }
  } catch (err) {
    console.error('Error fetching YAML title:', err);
    // Don't set error state for this - it's just a fallback
  }
};

// Fetch suites and topics from mod-content
const fetchDropdownData = async () => {
  try {
    const [
      suitesData,
      activityTypesData,
      accreditationsData,
      industriesData,
      jurisdictionsData,
      targetLearnersData
    ] = await Promise.all([
      getSuitesAndTopics(),
      getActivityTypes(),
      getAccreditations(),
      getIndustries(),
      getJurisdictions(),
      getTargetLearners()
    ]);

    // Separate suites (internalParentId: 0) from topics (internalParentId > 0)
    const allItems = suitesData as any[];
    suites.value = allItems.filter((item: any) => item.internalParentId === 0);
    topics.value = allItems.filter((item: any) => item.internalParentId > 0);
    activityTypes.value = (activityTypesData as any).data || [];
    accreditations.value = (accreditationsData as any).data || [];
    industries.value = (industriesData as any).data || {};
    jurisdictions.value = (jurisdictionsData as any).data || {};
    targetLearners.value = (targetLearnersData as any).data || [];
  } catch (err) {
    console.error('Error fetching dropdown data:', err);
    error.value = `Failed to load dropdown data: ${err.message}`;
  }
};

// Fetch all courses from Portal Library
const fetchAllLibraryCourses = async () => {
  try {
    loadingLibraryCourses.value = true;
    const response = await getAllCoursesFromLibrary();
    console.log('Raw API response:', response);
    console.log('Response data:', response.data);
    console.log('Response data.results:', response.data?.results);

    // Extract courses from the correct response structure
    allLibraryCourses.value = response.data?.data?.results || [];
    console.log(
      'All library courses loaded:',
      allLibraryCourses.value.length,
      'courses'
    );
    console.log('First course sample:', allLibraryCourses.value[0]);
  } catch (err) {
    console.error('Error fetching all library courses:', err);
    error.value = `Failed to load library courses: ${err.message}`;
  } finally {
    loadingLibraryCourses.value = false;
  }
};

// Helper function to merge draft data with proper section handling
const findPathByFieldName = (sectionName: string, fieldName: string) => {
  const resolvedFieldName =
    sectionName === 'sectionTitle' &&
    (fieldName === 'subTopicName' || fieldName === 'subtopicName')
      ? 'subtopicTitle'
      : fieldName;
  return Object.keys(FIELD_CONFIG).find(path => {
    const config = FIELD_CONFIG[path];
    const pathFieldName = path.split('.').pop();
    return (
      config.section === sectionName && pathFieldName === resolvedFieldName
    );
  });
};

const mergeDraftData = (draft: any, target: any) => {
  Object.entries(draft).forEach(([sectionName, sectionData]) => {
    if (sectionData && typeof sectionData === 'object') {
      Object.entries(sectionData).forEach(([fieldName, value]) => {
        if (value !== undefined) {
          const path = findPathByFieldName(sectionName, fieldName);
          if (path) {
            setNestedValue(target, path, value);
          } else {
            console.warn(
              `Could not find path for ${sectionName}.${fieldName} in FIELD_CONFIG. This field will not be loaded from draft.`
            );
          }
        }
      });
    }
  });
};

// Merge published and draft data into form
const mergeDataIntoForm = (published, draft) => {
  // Start with defaults, ensuring a deep copy to prevent mutation of formDefaults
  Object.assign(metadata, JSON.parse(JSON.stringify(formDefaults)));

  // Merge in published data, driven by FIELD_CONFIG
  if (published) {
    Object.keys(FIELD_CONFIG).forEach(path => {
      const value = getNestedValueWithDefaults(published, path);
      if (value !== undefined) {
        setNestedValue(metadata, path, value);
      }
    });

    // Extract HK hours from courseAccreditations (they're not directly in published data)
    // This is needed because HK hours are stored in courseAccreditations.HKCPD.CreditHours
    // and courseAccreditations.HKRME.CreditHours, not as direct fields
    const courseAccreditations = published.courseAccreditations || {};
    if (courseAccreditations.HKCPD?.CreditHours !== undefined) {
      (metadata as any).hkcpdHours = courseAccreditations.HKCPD.CreditHours;
    }
    if (courseAccreditations.HKRME?.CreditHours !== undefined) {
      (metadata as any).hkrmeHours = courseAccreditations.HKRME.CreditHours;
    }
  }

  // Override with draft data
  if (draft) {
    mergeDraftData(draft, metadata);
  }

  // Ensure TargetLearners is always an array (safety check after DB migration)
  if (metadata.resourceMaster?.TargetLearners !== undefined) {
    if (!Array.isArray(metadata.resourceMaster.TargetLearners)) {
      // Fallback: if somehow still a string, convert to array
      const tl = metadata.resourceMaster.TargetLearners;
      if (typeof tl === 'string' && tl.trim()) {
        metadata.resourceMaster.TargetLearners = tl
          .split(/,\s*|;\s*|\s+and\s+/i)
          .map(item => item.trim())
          .filter(item => item.length > 0);
      } else {
        metadata.resourceMaster.TargetLearners = [];
      }
    }
  }

  normalizeSectionTitleSubtopicFields(
    metadata as Record<string, unknown>,
    topics.value.find(t => t.title === metadata.topicTitle)?.internalId
  );

  // Fallback to YAML title if no title exists in metadata
  if (!metadata.title && yamlTitle.value) {
    // Using YAML title fallback
    metadata.title = yamlTitle.value;
  }

  // Determine initial title sync state based on existing data
  // If titles are different, start with sync disabled
  if (
    metadata.title &&
    metadata.portalTitle &&
    metadata.title !== metadata.portalTitle
  ) {
    titleSyncEnabled.value = false;
  } else {
    // Default to sync enabled, and ensure portal title matches course title
    titleSyncEnabled.value = true;
    if (metadata.title && !metadata.portalTitle) {
      metadata.portalTitle = metadata.title;
    }
  }

  // Title merge completed
};

// Calculate thumbnail section status based on S3 existence and generated thumbnails
const calculateThumbnailSectionStatus = (
  published: any,
  thumbnailExists: boolean = false
) => {
  if (!thumbnailExists) {
    return SECTION_STATUSES.NOT_STARTED;
  }

  if (hasGeneratedThumbnails.value) {
    return SECTION_STATUSES.READY_TO_PUBLISH;
  }

  if (thumbnailExists) {
    return SECTION_STATUSES.PUBLISHED;
  }

  return SECTION_STATUSES.NOT_STARTED;
};

// Calculate section status based on published vs draft data
// Logic: Check published data first, then draft data, then compare
const calculateSectionStatus = (
  sectionName: string,
  draft: any,
  published: any
) => {
  // Special handling for thumbnail section - it doesn't have traditional form fields
  if (sectionName === 'sectionThumbnails') {
    // Check if thumbnails exist on S3
    return calculateThumbnailSectionStatus(published, thumbnailExists.value);
  }

  const sectionFields = getFieldsForSection(sectionName);

  // Check if section has ANY published data (for determining if this is a new course vs existing)
  const hasAnyPublishedData = published && Object.keys(published).length > 0;

  // Check if section has ALL required fields in published data
  const hasAllRequiredPublishedData =
    published &&
    sectionFields.every(path => {
      const config = FIELD_CONFIG[path];
      // Only check required fields for published data
      if (!config.required) {
        return true; // Optional fields don't need to exist in published data
      }

      // Check if the path actually exists in the published data (not just default values)
      const keys = path.split('.');
      let current = published;
      for (const key of keys) {
        if (!current || typeof current !== 'object' || !(key in current)) {
          return false; // Path doesn't exist in published data
        }
        current = current[key];
      }

      const value = current;
      // Consider a field as having data if it's not null, undefined, or an empty string/array
      return (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        (!Array.isArray(value) || value.length > 0)
      );
    });

  // Check if section has draft data
  const hasDraftData =
    draft && draft[sectionName] && Object.keys(draft[sectionName]).length > 0;

  // If no published data exists at all, this is a new course
  if (!hasAnyPublishedData) {
    return hasDraftData
      ? SECTION_STATUSES.READY_TO_PUBLISH
      : SECTION_STATUSES.NOT_STARTED;
  }

  // If published data exists but not all required fields are present, this is an update
  if (!hasAllRequiredPublishedData) {
    return hasDraftData
      ? SECTION_STATUSES.READY_TO_UPDATE
      : SECTION_STATUSES.NOT_STARTED;
  }

  // All required fields are published, check if there are draft changes
  if (!hasDraftData) {
    return SECTION_STATUSES.PUBLISHED;
  }

  // Compare draft vs published to see if there are changes
  const draftSection = { ...draft[sectionName] };
  if (sectionName === 'sectionTitle') {
    const draftTopicId = topics.value.find(
      t => t.title === draftSection.topicTitle
    )?.internalId;
    normalizeSectionTitleSubtopicFields(draftSection, draftTopicId);
  }
  const publishedSection = extractPublishedSectionData(sectionName);

  // Use the same comparison logic as hasSectionChanges for consistency
  const hasChanges = compareObjects(draftSection, publishedSection);

  return hasChanges
    ? SECTION_STATUSES.READY_TO_UPDATE
    : SECTION_STATUSES.PUBLISHED;
};

// Update section status based on data
const updateSectionStatus = async () => {
  const draft = draftData.value;
  const published = publishedData.value;

  // Check thumbnail existence asynchronously
  try {
    // Check all thumbnail types
    const thumbnailTypes = [
      { name: 'standard', filename: `${props.courseId}.jpg` },
      { name: 'hero', filename: `${props.courseId}-hero.jpg` },
      { name: 'tile', filename: `${props.courseId}-tile.jpg` },
      { name: 'noTitle', filename: `${props.courseId}-notitle.jpg` }
    ];

    for (const thumbnail of thumbnailTypes) {
      thumbnailStatuses[thumbnail.name] = await checkThumbnailExists(
        thumbnail.filename
      );
    }

    // Set overall thumbnail exists flag (standard thumbnail)
    thumbnailExists.value = thumbnailStatuses.standard;
    console.log(
      `Thumbnail existence check for ${props.courseId}:`,
      thumbnailStatuses
    );
  } catch (error) {
    console.error('Error checking thumbnail existence:', error);
    thumbnailExists.value = false;
  }

  // Always calculate status for each section using the new calculation function
  // This handles all scenarios: no draft, no published, both, or neither
  Object.keys(sectionStatus).forEach(section => {
    // First check if there are unsaved changes (form data differs from saved draft data)
    if (hasSectionChanges(section)) {
      sectionStatus[section] = SECTION_STATUSES.UNSAVED_CHANGES;
    } else {
      // No unsaved changes, use the existing logic
      sectionStatus[section] = calculateSectionStatus(
        section,
        draft,
        published
      );
    }
  });
};

// Check if a section has unsaved changes
// Compare current form data against saved draft data
const hasSectionChanges = (sectionName: string) => {
  // Special handling for thumbnail section - it doesn't have traditional form fields
  if (sectionName === 'sectionThumbnails') {
    // Thumbnail section doesn't participate in form operations
    return false;
  }

  const draft = draftData.value;
  const published = publishedData.value;

  // If no draft data exists for this section
  if (!draft || !draft[sectionName]) {
    // Check if form data matches published data
    // If they match, there are no unsaved changes (it's Published)
    if (published) {
      const currentFormData = extractFormData(sectionName);
      const publishedSectionData = extractPublishedSectionData(sectionName);
      // compareObjects returns true if objects differ (have changes)
      // So if it returns false, they match (no unsaved changes)
      const hasChanges = compareObjects(currentFormData, publishedSectionData);
      return hasChanges; // false = matches published (no changes), true = differs (has changes)
    }
    // No published data either - check if form has any data to save
    return hasFormData(sectionName);
  }

  // Compare current form data against saved draft data
  const currentFormData = extractFormData(sectionName);
  const savedDraftData = { ...draft[sectionName] };

  if (sectionName === 'sectionTitle') {
    const draftTopicId = topics.value.find(
      t => t.title === savedDraftData.topicTitle
    )?.internalId;
    normalizeSectionTitleSubtopicFields(savedDraftData, draftTopicId);
  }

  // More robust comparison that handles Vue proxies and property order
  return compareObjects(currentFormData, savedDraftData);
};

// Check if a section has all required fields filled
const hasRequiredFields = (sectionName: string) => {
  // Special handling for thumbnail section - it doesn't have traditional form fields
  if (sectionName === 'sectionThumbnails') {
    // Thumbnail section doesn't participate in form operations
    return true;
  }

  const sectionFields = getFieldsForSection(sectionName);

  return sectionFields.every(path => {
    const config = FIELD_CONFIG[path];

    if (path === 'subtopicId' || path === 'subtopicTitle') {
      if (!topicHasSubtopics(selectedTopicInternalId.value)) {
        return true;
      }
      if (path === 'subtopicTitle') {
        return true;
      }
      const ids = metadata.subtopicId;
      const names = metadata.subtopicTitle;
      if (!Array.isArray(ids) || ids.length === 0) {
        return false;
      }
      if (!Array.isArray(names) || names.length !== ids.length) {
        return false;
      }
      return true;
    }

    if (!config.required) {
      return true; // Not required, so it's valid
    }

    const value = getNestedValueWithDefaults(metadata, path);

    // Special case for passMark, which is only required if assessment is scored
    if (
      path === 'passMark' &&
      getNestedValueWithDefaults(
        metadata,
        'resourceMaster.CompletionCriteria'
      ) !== 2
    ) {
      return true;
    }

    // Check for non-empty values
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (typeof value === 'number') {
      return value > 0; // For passmark, assuming other numbers are valid if not 0
    }
    return value !== null && value !== undefined;
  });
};

// Enhanced section validation - must have changes AND required fields
const canSaveSection = (sectionName: string) => {
  return hasSectionChanges(sectionName) && hasRequiredFields(sectionName);
};

// Determine if publish/update button should be enabled
const canPublish = computed(() => {
  const hasPublishedData =
    publishedData.value && Object.keys(publishedData.value).length > 0;

  // Filter out thumbnail section from publish validation
  const formSections = Object.keys(sectionStatus).filter(
    section => section !== 'sectionThumbnails'
  );

  if (!hasPublishedData) {
    // No published data exists - enable only when ALL form sections are "Ready To Publish"
    return formSections.every(
      section => sectionStatus[section] === SECTION_STATUSES.READY_TO_PUBLISH
    );
  } else {
    // Has published data - enable when AT LEAST ONE form section is "Ready To Update"
    return formSections.some(
      section => sectionStatus[section] === SECTION_STATUSES.READY_TO_UPDATE
    );
  }
});

// Check if reset buttons should be visible (only when there's published data)
const showResetButtons = computed(() => {
  return publishedData.value && Object.keys(publishedData.value).length > 0;
});

// Check if global reset form button should be visible (when any form section has changes)
const showGlobalResetButton = computed(() => {
  // Filter out thumbnail section from reset validation
  const formSections = Object.keys(sectionStatus).filter(
    section => section !== 'sectionThumbnails'
  );

  return (
    showResetButtons.value &&
    formSections.some(
      section => sectionStatus[section] === SECTION_STATUSES.READY_TO_UPDATE
    )
  );
});

// Check if a specific section's reset button should be visible
const canResetSection = (sectionName: string) => {
  // Special handling for thumbnail section - it doesn't participate in form operations
  if (sectionName === 'sectionThumbnails') {
    return false;
  }

  return (
    showResetButtons.value &&
    sectionStatus[sectionName] === SECTION_STATUSES.READY_TO_UPDATE
  );
};

// Extract current form data for a specific section
// Must match exactly what saveSection extracts for accurate comparison
const extractFormData = (sectionName: string) => {
  const sectionFields = getFieldsForSection(sectionName);
  const data: Record<string, unknown> = {};

  sectionFields.forEach(path => {
    let value = getNestedValue(metadata, path);
    const fieldName = path.split('.').pop();

    if (fieldName) {
      data[fieldName] = value;
    }
  });

  if (sectionName === 'sectionTitle') {
    normalizeSectionTitleSubtopicFields(data, selectedTopicInternalId.value);
  }

  return data;
};

// Check if form has any data for a section (for first-time saves)
const hasFormData = (sectionName: string) => {
  const formData = extractFormData(sectionName);
  return Object.values(formData).some(value => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== '' && value !== null && value !== undefined;
  });
};

// Robust object comparison (now imported from metadataUtils)

// Get CSS classes for save button based on state
const getSaveButtonClass = (sectionName: string) => {
  const isSaving = savingSections[sectionName];
  const canSave = canSaveSection(sectionName);

  if (isSaving || !canSave) {
    return 'opacity-50 cursor-not-allowed bg-gray-400';
  }

  return 'bg-primary-600';
};

// Save section data
const saveSection = async sectionName => {
  savingSections[sectionName] = true;

  try {
    // Extract section data from form using the new config-driven helper
    const sectionData = extractFormData(sectionName);

    // Get section title from config
    const sectionTitle = SECTION_CONFIG[sectionName]?.title || sectionName;

    await updateDraftMetadataSection(
      props.courseId,
      sectionName,
      sectionData,
      sectionTitle
    );

    // Refresh draft data to get updated metadata
    await fetchDraftData();
    await updateSectionStatus();
  } catch (err) {
    console.error(`Error saving ${sectionName}:`, err);
    error.value = `Failed to save ${sectionName}: ${err.message}`;
  } finally {
    savingSections[sectionName] = false;
  }
};

// Get status display text and color for new status values
const getStatusDisplay = status => {
  switch (status) {
    case SECTION_STATUSES.NOT_STARTED:
      return { text: 'Not Started', color: 'text-gray-400' };
    case SECTION_STATUSES.READY_TO_PUBLISH:
      return { text: 'Ready To Publish', color: 'text-blue-600' };
    case SECTION_STATUSES.READY_TO_UPDATE:
      return { text: 'Ready To Update', color: 'text-orange-600' };
    case SECTION_STATUSES.PUBLISHED:
      return { text: 'Published', color: 'text-green-700' };
    case SECTION_STATUSES.UNSAVED_CHANGES:
      return { text: 'Unsaved Changes', color: 'text-red-600' };
    default:
      return { text: 'Unknown', color: 'text-gray-500' };
  }
};

// Get badge class for new status values
const getStatusBadgeClass = status => {
  switch (status) {
    case SECTION_STATUSES.NOT_STARTED:
      return 'bg-gray-200 text-gray-800';
    case SECTION_STATUSES.READY_TO_PUBLISH:
      return 'bg-blue-100 text-blue-800';
    case SECTION_STATUSES.READY_TO_UPDATE:
      return 'bg-orange-100 text-orange-800';
    case SECTION_STATUSES.PUBLISHED:
      return 'bg-green-100 text-green-800';
    case SECTION_STATUSES.UNSAVED_CHANGES:
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-200 text-gray-800';
  }
};

// Publish modal state (UI-only for step 1-2)
const showPublishModal = ref(false);
const isPublishing = ref(false);

// Thumbnail generation modal state
const showThumbnailGenerationModal = ref(false);
const isUpdateMode = computed(
  () => !!(publishedData.value && Object.keys(publishedData.value).length > 0)
);
const showDeleteModal = ref(false);
const isDeleting = ref(false);

// Diff summary for Update modal
const publishDiff = ref<
  Record<string, { field: string; oldVal: any; newVal: any }[]>
>({});

// Strip HTML tags (now imported from metadataUtils)

const areValuesEqual = (a: any, b: any) => {
  // Handle null/undefined/empty string equivalence - they should be considered equal for change detection
  const normalizeEmptyValue = (val: any) => {
    if (val === null || val === undefined || val === '') return null;
    return val;
  };

  const normalizedA = normalizeEmptyValue(a);
  const normalizedB = normalizeEmptyValue(b);

  if (normalizedA === normalizedB) return true;

  if (Array.isArray(normalizedA) && Array.isArray(normalizedB)) {
    if (normalizedA.length !== normalizedB.length) return false;
    for (let i = 0; i < normalizedA.length; i++)
      if (normalizedA[i] !== normalizedB[i]) return false;
    return true;
  }
  const sa = stripHtml(normalizedA);
  const sb = stripHtml(normalizedB);
  return sa === sb;
};

const normSubtopicListForPublishDiff = (val: any) => {
  if (val == null) return '';
  if (!Array.isArray(val)) return String(val);
  if (val.length === 0) return '';
  return [...val].map(String).sort().join('\0');
};

const computeDiffBySection = () => {
  const result: Record<string, { field: string; oldVal: any; newVal: any }[]> =
    {};

  Object.keys(SECTION_CONFIG).forEach(sectionName => {
    const current = extractFormData(sectionName as any);
    const published = extractPublishedSectionData(sectionName as any);

    const fields = new Set([
      ...Object.keys(current || {}),
      ...Object.keys(published || {})
    ]);
    fields.delete('subtopicId');

    const diffs: { field: string; oldVal: any; newVal: any }[] = [];

    fields.forEach(field => {
      const oldVal = (published as any)?.[field];
      const newVal = (current as any)?.[field];

      if (field === 'subtopicTitle') {
        const oldIds = (published as any)?.subtopicId;
        const newIds = (current as any)?.subtopicId;
        const equal =
          normSubtopicListForPublishDiff(newIds) ===
          normSubtopicListForPublishDiff(oldIds);
        if (!equal) {
          diffs.push({
            field: 'subtopicTitle',
            oldVal: (published as any)?.subtopicTitle,
            newVal: (current as any)?.subtopicTitle
          });
        }
        return;
      }

      if (!areValuesEqual(newVal, oldVal)) {
        diffs.push({ field, oldVal, newVal });
      }
    });

    if (diffs.length > 0) result[sectionName] = diffs;
  });

  publishDiff.value = result;
};

const handlePublishUpdate = () => {
  if (isUpdateMode.value) computeDiffBySection();
  showPublishModal.value = true;
};

// Build courseAccreditations object based on HK Law Society selection
const buildCourseAccreditations = () => {
  const accreditations: any = {};

  // Only include HK accreditations if HK Law Society is selected
  if (isHKLawSocietySelected.value) {
    const courseVersionId = ensureCourseversionID();

    // Add HKCPD accreditation if hkcpdHours is set
    if (metadata.hkcpdHours !== null && metadata.hkcpdHours !== undefined) {
      accreditations.HKCPD = {
        ...courseAccreditationTemplates.HKCPD,
        CreditHours: metadata.hkcpdHours,
        CourseVersionId: courseVersionId
      };
    }

    // Add HKRME accreditation if hkrmeHours is set
    if (metadata.hkrmeHours !== null && metadata.hkrmeHours !== undefined) {
      accreditations.HKRME = {
        ...courseAccreditationTemplates.HKRME,
        CreditHours: metadata.hkrmeHours,
        CourseVersionId: courseVersionId
      };
    }
  }

  return accreditations;
};

const closePublishModal = () => {
  if (!isPublishing.value) {
    showPublishModal.value = false;
  }
};

const confirmPublish = async () => {
  try {
    isPublishing.value = true;

    // Minimal payload to confirm frontend → mod-content pipe
    const payloadToNormalize = {
      vinciResourceId: props.courseId,
      firmCode: props.firmCode,
      tenantId: props.firmCode,
      title: metadata.title,
      portalTitle: metadata.portalTitle,
      flagship: String(metadata.flagship),
      vendor: metadata.vendor,
      suiteTitle: metadata.suiteTitle,
      topicTitle: metadata.topicTitle,
      suiteId:
        suites.value.find(s => s.title === metadata.suiteTitle)?.internalId ||
        null,
      topicId:
        topics.value.find(t => t.title === metadata.topicTitle)?.internalId ||
        null,
      subtopicId: topicHasSubtopics(selectedTopicInternalId.value)
        ? [...(Array.isArray(metadata.subtopicId) ? metadata.subtopicId : [])]
        : null,
      subtopicTitle: topicHasSubtopics(selectedTopicInternalId.value)
        ? [
            ...(Array.isArray(metadata.subtopicTitle)
              ? metadata.subtopicTitle
              : [])
          ]
        : null,
      level: metadata.level,
      duration: metadata.duration,
      passMark: metadata.passMark,
      accredited: metadata.accredited,
      activityName: metadata.activityName,
      resourceType: 'SCORM',
      vendorResourceCode: `VW-${props.courseId}`,
      additionalInfo: {
        Industry: metadata.additionalInfo?.Industry || 'All industries',
        industryId: metadata.additionalInfo?.industryId || '001',
        CourseFeatures: metadata.additionalInfo?.CourseFeatures || [],
        Jurisdiction: metadata.additionalInfo?.Jurisdiction || [],
        jurisdictionId: metadata.additionalInfo?.jurisdictionId || [],
        Languages: metadata.additionalInfo?.Languages || [],
        CourseDescription: metadata.description || '',
        ExcludeFromDemo:
          typeof metadata.additionalInfo?.ExcludeFromDemo === 'boolean'
            ? metadata.additionalInfo.ExcludeFromDemo
            : false,
        triggerWarning: metadata.additionalInfo?.triggerWarning || false,
        qualityIndex: metadata.additionalInfo?.qualityIndex
          ? parseInt(metadata.additionalInfo.qualityIndex, 10)
          : null,
        groupCourseId: (() => {
          // If groupCourseId is empty, null, or undefined, use current course ID
          const currentGroupId = metadata.additionalInfo?.groupCourseId;
          if (
            !currentGroupId ||
            currentGroupId === null ||
            currentGroupId === undefined ||
            (Array.isArray(currentGroupId) && currentGroupId.length === 0)
          ) {
            return props.courseId;
          }
          return currentGroupId;
        })(),
        relatedTo: metadata.additionalInfo?.relatedTo || [],
        redundancy: metadata.additionalInfo?.redundancy || [],
        legislation: metadata.additionalInfo?.legislation || null
      },

      description: metadata.description || '',
      shortDescription: metadata.shortDescription || '',
      objectives: metadata.objectives || '',
      tagline: metadata.tagline || '',
      howTaken: metadata.howTaken || '',
      retired: typeof metadata.retired === 'boolean' ? metadata.retired : false,
      replacedBy:
        metadata.replacedBy === null || metadata.replacedBy === undefined
          ? null
          : Number(metadata.replacedBy),
      packagesIds: [],
      courseAccreditations: buildCourseAccreditations(),
      resourceMaster: {
        CompletionCriteria: metadata.resourceMaster.CompletionCriteria,
        TargetLearners: metadata.resourceMaster.TargetLearners,
        PresentersBio: metadata.resourceMaster?.PresentersBio || '',
        Version: metadata.resourceMaster?.Version || 'Current',
        CourseversionID: ensureCourseversionID()
      }
    };

    if (!publishedData.value || Object.keys(publishedData.value).length === 0) {
      await publishCourseToLibrary(payloadToNormalize);
    } else {
      await updateCourseInLibrary(props.courseId, payloadToNormalize);
    }

    // Stop loading before closing so the modal can close immediately
    isPublishing.value = false;
    closePublishModal();

    // Refresh published data
    await fetchPublishedData();
    // Sync draft to published so sections flip to Published and save buttons disable
    const formSections = Object.keys(SECTION_CONFIG).filter(
      section => section !== 'sectionThumbnails'
    );

    for (const sectionName of formSections) {
      const publishedSectionData = extractPublishedSectionData(
        sectionName as any
      );

      // Get section title from config
      const sectionTitle = SECTION_CONFIG[sectionName]?.title || sectionName;

      await updateDraftMetadataSection(
        props.courseId,
        sectionName,
        publishedSectionData,
        sectionTitle,
        true // isPublishSync = true when syncing after publish
      );
    }
    await fetchDraftData();
    await updateSectionStatus();
    error.value = '';
  } catch (err) {
    // Sanitize error message
    let message = 'Failed to publish course';
    // @ts-ignore
    if (err?.response?.data?.message) {
      // @ts-ignore
      message = `Publish failed: ${err.response.data.message}`;
      // @ts-ignore
    } else if (err?.response?.data?.error) {
      // @ts-ignore
      message = `Publish failed: ${err.response.data.error}`;
    } else if (err instanceof Error && err.message) {
      message = `Publish failed: ${err.message}`;
    }
    error.value = message;
    // Keep modal open so user can see the error
  } finally {
    // Ensure loading flag is cleared if we errored before clearing above
    isPublishing.value = false;
  }
};

const openDeleteModal = () => {
  showDeleteModal.value = true;
};
const closeDeleteModal = () => {
  showDeleteModal.value = false;
};
const confirmDelete = async () => {
  try {
    isDeleting.value = true;
    await deleteCourseFromLibrary(props.courseId);
    // Refresh published data to reflect deletion
    await fetchPublishedData();
    await updateSectionStatus();
  } catch (err) {
    console.error('Failed to delete course:', err);
    error.value = 'Failed to delete course';
  } finally {
    isDeleting.value = false;
    closeDeleteModal();
  }
};

// Placeholder function for form-wide reset
const fakeResetForm = () => {
  alert('Reset Form clicked');
};

// Placeholder function for section-specific reset
const fakeResetSection = (sectionName: string) => {
  alert(`Reset ${sectionName} clicked`);
};

// Extract published data for a specific section (for reset functionality)
const extractPublishedSectionData = (sectionName: string) => {
  if (!publishedData.value) return {};

  const sectionFields = getFieldsForSection(sectionName);
  const data: Record<string, unknown> = {};

  sectionFields.forEach(path => {
    // Get raw value from published data without default fallback
    const value = path
      .split('.')
      .reduce((acc, part) => acc && acc[part], publishedData.value);
    const fieldName = path.split('.').pop();
    if (fieldName) {
      data[fieldName] = value; // This will be undefined if the field doesn't exist in published data
    }
  });

  // For sectionFeatures, also extract HK hours from published courseAccreditations
  if (sectionName === 'sectionFeatures') {
    const courseAccreditations =
      publishedData.value?.courseAccreditations || {};
    if (courseAccreditations.HKCPD?.CreditHours !== undefined) {
      data.hkcpdHours = courseAccreditations.HKCPD.CreditHours;
    }
    if (courseAccreditations.HKRME?.CreditHours !== undefined) {
      data.hkrmeHours = courseAccreditations.HKRME.CreditHours;
    }
  }

  if (sectionName === 'sectionTitle') {
    normalizeSectionTitleSubtopicFields(data, publishedData.value?.topicId);
  }

  return data;
};

// Update form with published data for a specific section
const updateFormWithPublishedData = (
  sectionName: string,
  dataToRestore: any
) => {
  const sectionFields = getFieldsForSection(sectionName);

  sectionFields.forEach(path => {
    const fieldName = path.split('.').pop();
    if (!fieldName) return;

    let value = dataToRestore[fieldName];

    // Use the default value from config if the restored value is undefined
    if (value === undefined) {
      value = FIELD_CONFIG[path].defaultValue;
    }

    setNestedValue(metadata, path, value);
  });

  // Reset title sync toggle when resetting sectionTitle
  if (sectionName === 'sectionTitle') {
    // Determine toggle state based on restored data
    if (
      metadata.title &&
      metadata.portalTitle &&
      metadata.title !== metadata.portalTitle
    ) {
      titleSyncEnabled.value = false;
    } else {
      titleSyncEnabled.value = true;
      if (metadata.title && !metadata.portalTitle) {
        metadata.portalTitle = metadata.title;
      }
    }
  }
};

// Reset a specific section to published values
const resetSection = async (sectionName: string) => {
  try {
    // Extract published data for this section
    const publishedSectionData = extractPublishedSectionData(sectionName);

    // Update the form with published values
    updateFormWithPublishedData(sectionName, publishedSectionData);

    // Get section title from config
    const sectionTitle = SECTION_CONFIG[sectionName]?.title || sectionName;

    // Instead of clearing draft data, update it with the published values
    // This effectively "resets" the section by making draft match published
    await updateDraftMetadataSection(
      props.courseId,
      sectionName,
      publishedSectionData,
      sectionTitle
    );

    // Refresh draft data and update statuses
    await fetchDraftData();
    await updateSectionStatus();
  } catch (err) {
    console.error(`Error resetting ${sectionName}:`, err);
    throw err;
  }
};

// Reset all form sections that have changes
const resetAllSections = async () => {
  try {
    // Get all form sections that are "Ready To Update" (exclude thumbnails)
    const sectionsToReset = Object.keys(sectionStatus).filter(
      section =>
        section !== 'sectionThumbnails' &&
        sectionStatus[section] === SECTION_STATUSES.READY_TO_UPDATE
    );

    // Reset each section sequentially
    for (const sectionName of sectionsToReset) {
      await resetSection(sectionName);
    }
  } catch (err) {
    console.error('Error resetting all sections:', err);
    throw err;
  }
};

// Manual toggle for compact view
const isCompact = ref(true);
const toggleCompact = () => {
  isCompact.value = !isCompact.value;
};

// Reset confirmation modal state
const showResetModal = ref(false);
const resetModalTitle = ref('');
const resetTarget = ref<string>(''); // 'all' or section name

const openResetModal = (target: string) => {
  resetTarget.value = target;
  if (target === 'all') {
    resetModalTitle.value = 'Reset All Sections?';
  } else {
    resetModalTitle.value = `Reset ${SECTION_CONFIG[target].title} Section?`;
  }
  showResetModal.value = true;
};

const closeResetModal = () => {
  showResetModal.value = false;
  resetTarget.value = '';
};

const confirmReset = async () => {
  try {
    if (resetTarget.value === 'all') {
      await resetAllSections();
    } else {
      await resetSection(resetTarget.value);
    }
    closeResetModal();
  } catch (err) {
    console.error('Error resetting:', err);
    error.value = `Failed to reset: ${err.message}`;
  }
};

// Thumbnail generation modal functions
const openThumbnailGenerationModal = () => {
  showThumbnailGenerationModal.value = true;
};

const closeThumbnailGenerationModal = () => {
  showThumbnailGenerationModal.value = false;
};

const handleThumbnailsGenerated = (thumbnails: any[]) => {
  console.log('Thumbnails generated:', thumbnails);
  // Store generated thumbnails in memory
  generatedThumbnails.value = thumbnails;
  console.log('Generated thumbnails stored:', generatedThumbnails.value);

  // Update section status to reflect new thumbnails are ready
  updateSectionStatus();
};

// Cancel generated thumbnails and reload S3 images
const cancelGeneratedThumbnails = async () => {
  console.log('Canceling generated thumbnails');
  generatedThumbnails.value = [];

  // Reload S3 thumbnail status
  await updateSectionStatus();
  console.log('Generated thumbnails cleared, S3 status refreshed');
};

// Upload generated thumbnails to S3
const uploadGeneratedThumbnails = async () => {
  console.log('Uploading generated thumbnails:', generatedThumbnails.value);
  console.log(
    'Number of thumbnails to upload:',
    generatedThumbnails.value.length
  );

  isUploadingThumbnails.value = true;

  try {
    // Upload each generated thumbnail
    for (const thumbnail of generatedThumbnails.value) {
      console.log(`Uploading ${thumbnail.type} thumbnail:`, thumbnail.filename);
      console.log('Thumbnail blob size:', thumbnail.blob.size);

      try {
        await uploadThumbnail(thumbnail.blob, thumbnail.filename);
        console.log(`✅ Successfully uploaded ${thumbnail.type} thumbnail`);
      } catch (uploadError) {
        console.error(
          `❌ Failed to upload ${thumbnail.type} thumbnail:`,
          uploadError
        );
        throw uploadError; // Re-throw to trigger the outer catch
      }
    }

    console.log('All thumbnails uploaded successfully');

    // Show success state for 2 seconds
    isUploadingThumbnails.value = false;
    uploadSuccess.value = true;
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Clear generated thumbnails from memory
    generatedThumbnails.value = [];

    // Update cache-buster to force thumbnail refresh
    thumbnailCacheBuster.value = Date.now();

    // Refresh S3 status to show uploaded thumbnails
    await updateSectionStatus();
  } catch (error) {
    console.error('Error uploading thumbnails:', error);
    error.value = `Failed to upload thumbnails: ${error.message}`;
  } finally {
    isUploadingThumbnails.value = false;
    uploadSuccess.value = false;
  }
};

// Load data on component mount
onMounted(async () => {
  loading.value = true;
  error.value = '';

  try {
    // Fetch dropdown data first so it's available when merging form data
    await fetchDropdownData();

    // Fetch all library courses for relationship selection
    await fetchAllLibraryCourses();

    // Fetch YAML title so it's available as fallback
    await fetchYamlTitle();

    const [published, draft] = await Promise.all([
      fetchPublishedData(),
      fetchDraftData()
    ]);

    mergeDataIntoForm(published, draft);

    // Manually trigger passmark logic after data is loaded
    if (metadata.resourceMaster.CompletionCriteria === 1) {
      if (metadata.passMark > 0) {
        originalPassmark.value = metadata.passMark;
      }
      metadata.passMark = 0;
    }

    await updateSectionStatus();
  } catch (err) {
    console.error('Error loading data:', err);
    error.value = `Failed to load data: ${err.message}`;
  } finally {
    loading.value = false;
    // Mark data loading as complete to enable watchers
    isDataLoaded.value = true;
  }
});
</script>

<style scoped>
/* Add any component-specific styles here */

/* Required field asterisk styling */
.required-asterisk {
  font-weight: bold;
  margin-left: 2px;
}

/* Enhanced form validation visual feedback */
.form-section input:invalid,
.form-section select:invalid,
.form-section textarea:invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 1px #ef4444;
}

/* Focus states for better accessibility */
.form-section input:focus,
.form-section select:focus,
.form-section textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

/* Reset confirmation modal styles */
.modal-overlay {
  backdrop-filter: blur(2px);
}

.modal-content {
  max-height: 90vh;
  overflow-y: auto;
}

/* Reset confirmation button - override global .btn styles */
.reset-confirm-btn {
  background-color: #dc2626 !important; /* bg-red-600 */
  color: white !important;
  padding: 0.5rem 1rem !important;
  border-radius: 0.25rem !important;
  font-weight: 500 !important;
  transition: all 0.2s !important;
}

.reset-confirm-btn:hover {
  background-color: #b91c1c !important; /* bg-red-700 */
}

.reset-confirm-btn:active {
  background-color: #991b1b !important; /* bg-red-800 */
}
</style>
